import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button, Descriptions, Tag, message, Card, Spin, Space, Popconfirm,
  Modal, Form, Input, Select, Table, Checkbox, Tabs, Tooltip, Badge, Upload,
} from 'antd';
import {
  ArrowLeftOutlined, CloudDownloadOutlined, DownOutlined,
  EditOutlined, SaveOutlined, PlusOutlined, DeleteOutlined, EyeOutlined, InfoCircleOutlined, ImportOutlined,
} from '@ant-design/icons';
import { employeesApi, skillsApi, toolsApi, mcpServersApi, documentsApi, expertsApi, dictionariesApi, uploadApi, featureConfigsApi, agentConfigApi } from '../services/api-services';
import { useTabStore } from '../store/tabStore';
import { resolveUrl } from '../utils/url';
import { useTheme } from '../contexts/ThemeContext';
import { useStreamGeneration } from '../hooks/useStreamGeneration';
import AppFileManager from '../components/AppFileManager';
import IniFormView, { IniFormViewRef } from '../components/IniFormView';
import GatewayTab from '../components/GatewayTab';
import ModelConfigModal from '../components/ModelConfigModal';
import MdEditor from 'react-markdown-editor-lite';
import MarkdownIt from 'markdown-it';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import 'react-markdown-editor-lite/lib/index.css';
import '../styles/markdown.css';
import ImagePicker from '../components/ImagePicker';

const { TextArea } = Input;
const mdParser = new MarkdownIt();

const EMPLOYEE_TYPE_OPTIONS = [
  { label: 'Manager', value: 'manager' },
  { label: 'Technical', value: 'technical' },
];

const ResourceSection: React.FC<{
  title: string;
  linked: any[];
  all: any[];
  onAdd: (ids: number[]) => void;
  onRemove: (id: number) => void;
  onUpgrade?: (id: number) => void;
  onView?: (record: any) => void;
  extraActions?: React.ReactNode;
  nameKey?: string;
  descKey?: string;
  showVersion?: boolean;
  resourceType?: string;
  resourceVersions?: Record<string, number>;
  updates?: any[];
}> = ({ title, linked, all, onAdd, onRemove, onUpgrade, onView, extraActions, nameKey = 'name', descKey = 'description', showVersion = true, resourceType, resourceVersions, updates }) => {
  const [addVisible, setAddVisible] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);

  const linkedIds = new Set(linked.map((x: any) => x.id));
  const available = all.filter((x: any) => !linkedIds.has(x.id));

  const handleAdd = () => {
    if (selected.length > 0) onAdd(selected);
    setSelected([]);
    setAddVisible(false);
  };

  const columns: any[] = [
    { title: '名称', dataIndex: nameKey, key: 'name', width: 160 },
    {
      title: '描述', key: 'desc',
      render: (_: any, record: any) => (
        <span style={{ color: 'var(--apple-text-secondary)', fontSize: 13 }}>
          {record[descKey] || '-'}
        </span>
      ),
    },
    ...(showVersion ? [{
      title: '版本', key: 'version', width: 160,
      render: (_: any, record: any) => {
        const linkedVersion = resourceVersions?.[String(record.id)];
        const updateInfo = updates?.find((u: any) => u.id === record.id);
        return (
          <Space size={4}>
            <Tag color={updateInfo ? 'orange' : 'blue'}>v{linkedVersion ?? record.version}</Tag>
            {updateInfo && (
              <Tag color="green" style={{ cursor: 'pointer' }} onClick={() => onUpgrade?.(record.id)}>
                → v{updateInfo.current_version}
              </Tag>
            )}
          </Space>
        );
      },
    }] : []),
    {
      title: '操作', key: 'action', width: onView ? 120 : 80,
      render: (_: any, record: any) => (
        <Space size={0}>
          {onView && (
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => onView(record)} />
          )}
          <Popconfirm title="确定移除?" onConfirm={() => onRemove(record.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={title}
      extra={<Space size={8}>{extraActions}<Button size="small" icon={<PlusOutlined />} onClick={() => setAddVisible(true)}>添加</Button></Space>}
    >
      <Table
        size="small"
        dataSource={linked}
        columns={columns}
        rowKey="id"
        pagination={false}
        locale={{ emptyText: '暂无关联' }}
      />
      <Modal
        className="resource-add-modal"
        title={`添加 ${title}`}
        open={addVisible}
        onCancel={() => { setAddVisible(false); setSelected([]); }}
        onOk={handleAdd}
        okText="添加"
        destroyOnHidden
      >
        {available.length === 0 ? (
          <p style={{ color: 'var(--apple-text-secondary)' }}>没有可添加的项目</p>
        ) : (
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {available.map((item: any) => (
              <div key={item.id} className="resource-add-option">
                <Checkbox
                  checked={selected.includes(item.id)}
                  onChange={(e) => setSelected(e.target.checked
                    ? [...selected, item.id]
                    : selected.filter((id) => id !== item.id))}
                >
                  <span>{item[nameKey]}</span>
                  {item[descKey] && <span style={{ marginLeft: 8, color: 'var(--apple-text-secondary)', fontSize: 12 }}>{item[descKey]}</span>}
                </Checkbox>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </Card>
  );
};

const ModelConfigTab: React.FC<{ id: number }> = ({ id }) => {
  const { theme } = useTheme();
  const isDark = theme === 'glass';
  const selectStyle: React.CSSProperties = {
    padding: '6px 10px', borderRadius: 6, fontSize: 14,
    background: isDark ? '#1a1f2e' : '#fff',
    color: isDark ? '#e0e0e0' : '#000',
    border: `1px solid ${isDark ? '#3a3f52' : '#d9d9d9'}`,
  };
  const [profiles, setProfiles] = useState<any[]>([]);
  const [activeProfile, setActiveProfile] = useState('');
  const [selected, setSelected] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      agentConfigApi.getGlobalConfig(),
      agentConfigApi.getEmployeeConfig(id),
    ]).then(([globalRes, empRes]: any) => {
      setProfiles(globalRes.profiles || []);
      setActiveProfile(globalRes.active_profile || '');
      const appliedProfile = empRes.profile || globalRes.active_profile || '';
      setSelected(appliedProfile);
      const actualModel = empRes.model || '';
      const active = (globalRes.profiles || []).find((p: any) => p.name === appliedProfile);
      setSelectedModel(actualModel || active?.default_model || '');
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  const profile = profiles.find((p) => p.name === selected);
  const modelOptions: string[] = profile?.models?.length ? profile.models : (profile?.default_model ? [profile.default_model] : []);

  const handleSelectProfile = (name: string) => {
    setSelected(name);
    const p = profiles.find((x) => x.name === name);
    setSelectedModel(p?.default_model || '');
  };

  const handleApply = async () => {
    if (!selected) return;
    setApplying(true);
    try {
      await agentConfigApi.applyProfileToEmployee(id, selected, selectedModel);
      // 同步更新本地 profiles 中该 profile 的 default_model，保证回显正确
      setProfiles((prev) => prev.map((p) => p.name === selected ? { ...p, default_model: selectedModel } : p));
      message.success('应用成功');
    } catch (e: any) {
      message.error(e.response?.data?.detail || '应用失败');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Card loading={loading}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <select
          value={selected}
          onChange={(e) => handleSelectProfile(e.target.value)}
          style={{ ...selectStyle, flex: 1 }}
        >
          <option value="">请选择配置</option>
          {profiles.map((p) => (
            <option key={p.name} value={p.name}>{p.name}{p.name === activeProfile ? ' (默认)' : ''}</option>
          ))}
        </select>
        <Button type="primary" loading={applying} disabled={!selected} onClick={handleApply}>
          应用
        </Button>
      </div>
      {profile && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <tbody>
            {[
              ['Provider', profile.provider],
              ['Base URL', profile.base_url],
              ['Token', profile.token ? profile.token.slice(0, 8) + '...' : '-'],
            ].map(([label, value]) => (
              <tr key={label} style={{ borderBottom: `1px solid ${isDark ? '#2a2f42' : '#f0f0f0'}` }}>
                <td style={{ padding: '8px 12px', color: 'var(--apple-text-secondary)', width: 120 }}>{label}</td>
                <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{value || '-'}</td>
              </tr>
            ))}
            <tr style={{ borderBottom: `1px solid ${isDark ? '#2a2f42' : '#f0f0f0'}` }}>
              <td style={{ padding: '8px 12px', color: 'var(--apple-text-secondary)', width: 120 }}>模型</td>
              <td style={{ padding: '8px 12px' }}>
                {modelOptions.length > 1 ? (
                  <select
                    value={selectedModel}
                    onChange={(e) => setSelectedModel(e.target.value)}
                    style={{ ...selectStyle, fontSize: 13, fontFamily: 'monospace', width: 360 }}
                  >
                    {modelOptions.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                ) : (
                  <span style={{ fontFamily: 'monospace' }}>{selectedModel || '-'}</span>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      )}
      {profiles.length === 0 && !loading && (
        <div style={{ color: 'var(--apple-text-secondary)' }}>暂无配置，请先在「大模型配置」页面创建</div>
      )}
    </Card>
  );
};

const ExpandableText: React.FC<{ text: string; limit?: number }> = ({ text, limit = 450 }) => {
  const [expanded, setExpanded] = React.useState(false);
  if (!text || text.length <= limit) return <span style={{ fontSize: 13 }}>{text}</span>;
  return (
    <span style={{ fontSize: 13 }}>
      {expanded ? text : text.slice(0, limit) + '…'}
      <span style={{ color: '#1677ff', cursor: 'pointer', marginLeft: 4, fontSize: 12 }} onClick={() => setExpanded(v => !v)}>
        {expanded ? '收起' : '展开'}
      </span>
    </span>
  );
};

const EmployeeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addTab } = useTabStore();
  const { generate: streamGenerate } = useStreamGeneration();
  const [employee, setEmployee] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedSoul, setEditedSoul] = useState('');
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();
  const [fileRefreshKey, setFileRefreshKey] = useState(0);
  const installStatusTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [employeeTypeOptions, setEmployeeTypeOptions] = useState<{ label: string; value: string }[]>(EMPLOYEE_TYPE_OPTIONS);
  const [employeeCategoryOptions, setEmployeeCategoryOptions] = useState<{ label: string; value: string }[]>([]);
  const [acpBackendOptions, setAcpBackendOptions] = useState<{ label: string; value: string }[]>([
    { value: 'hermes', label: 'Hermes' },
    { value: 'kiro', label: 'Kiro CLI' },
    { value: 'copilot', label: 'GitHub Copilot' },
    { value: 'claudecode', label: 'Claude Code' },
    { value: 'codex', label: 'OpenAI Codex' },
    { value: 'gemini', label: 'Gemini CLI' },
    { value: 'cursor', label: 'Cursor Agent' },
    { value: 'kimi', label: 'Kimi CLI' },
    { value: 'opencode', label: 'OpenCode' },
  ]);

  // When MdEditor goes fullscreen, backdrop-filter on .apple-content breaks position:fixed.
  // Toggle a class on <html> so CSS can disable it.
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const isFullscreen = !!document.querySelector('.rc-md-editor.full');
      document.documentElement.classList.toggle('md-fullscreen', isFullscreen);
    });
    observer.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['class'] });
    return () => {
      observer.disconnect();
      document.documentElement.classList.remove('md-fullscreen');
    };
  }, []);

  const [envContent, setEnvContent] = useState('');
  const [previewSkill, setPreviewSkill] = useState<any>(null);
  const [envLoading, setEnvLoading] = useState(false);
  const iniFormRef = useRef<IniFormViewRef>(null);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [featureConfigs, setFeatureConfigs] = useState<any[]>([]);



  const [allSkills, setAllSkills] = useState<any[]>([]);
  const [allTools, setAllTools] = useState<any[]>([]);
  const [allMcp, setAllMcp] = useState<any[]>([]);
  const [allDocs, setAllDocs] = useState<any[]>([]);
  const [allExperts, setAllExperts] = useState<any[]>([]);
  const [expertPickerVisible, setExpertPickerVisible] = useState(false);
  const [modelModalOpen, setModelModalOpen] = useState(false);
  const [currentModelLabel, setCurrentModelLabel] = useState('');
  const [expertPickerAvailable, setExpertPickerAvailable] = useState<any[]>([]);
  const [expertPickerSelected, setExpertPickerSelected] = useState<number[]>([]);
  const [hoveredExpertId, setHoveredExpertId] = useState<number | null>(null);
  const [allEmployees, setAllEmployees] = useState<any[]>([]);
  const [resourceUpdates, setResourceUpdates] = useState<any[]>([]);
  const [commandAllowlist, setCommandAllowlist] = useState<string[]>([]);
  const [permissionRequests, setPermissionRequests] = useState<any[]>([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [res, allowlistRes, permReqs]: any = await Promise.all([
        employeesApi.get(Number(id)),
        employeesApi.getCommandAllowlist(Number(id)).catch(() => ({ command_allowlist: [] })),
        employeesApi.listPermissionRequests(Number(id)).catch(() => []),
      ]);
      setEmployee(res);
      setCommandAllowlist(allowlistRes.command_allowlist || []);
      setPermissionRequests(permReqs || []);
      addTab({ key: `/employees/${id}`, path: `/employees/${id}`, label: `详情:${res.name}`, closable: true });
      // 加载当前模型
      Promise.all([agentConfigApi.getGlobalConfig(), agentConfigApi.getEmployeeConfig(Number(id))])
        .then(([globalRes, empRes]: any) => {
          const profs = globalRes.profiles || [];
          const isHermes = (empRes.acp_backend || 'hermes') === 'hermes';
          const appliedProfile = isHermes ? (empRes.profile || globalRes.active_profile || '') : '';
          const active = profs.find((p: any) => p.name === appliedProfile);
          const model = empRes.model || active?.default_model || '';
          setCurrentModelLabel(model);
        }).catch(() => {});
    } catch {
      message.error('获取数字人详情失败');
    }
    setLoading(false);
  }, [id]);

  const fetchUpdates = useCallback(async () => {
    try {
      const res: any = await employeesApi.checkUpdates(Number(id));
      setResourceUpdates(res);
    } catch { /* ignore */ }
  }, [id]);

  const fetchAllResources = async () => {
    try {
      const [s, t, m, d, e, emp] = await Promise.all([
        skillsApi.list() as any,
        toolsApi.list() as any,
        mcpServersApi.list() as any,
        documentsApi.list() as any,
        expertsApi.list() as any,
        employeesApi.list() as any,
      ]);
      setAllSkills(s);
      setAllTools(t);
      setAllMcp(m);
      setAllDocs(d);
      setAllExperts(e);
      setAllEmployees(emp);
    } catch { /* silently fail */ }
    try {
      const dict: any = await dictionariesApi.getByCode('employee_type');
      if (dict && dict.items) {
        setEmployeeTypeOptions(dict.items.map((item: any) => ({ label: item.name, value: item.value })));
      }
    } catch { /* use defaults */ }
    try {
      const dict: any = await dictionariesApi.getByCode('employee_category');
      if (dict?.items) setEmployeeCategoryOptions(dict.items.map((i: any) => ({ label: i.name, value: i.value })));
    } catch { /* use free input */ }
    try {
      const dict: any = await dictionariesApi.getByCode('acp_backend');
      if (dict?.items) setAcpBackendOptions(dict.items.map((i: any) => ({ label: i.name, value: i.value })));
    } catch { /* keep defaults */ }
  };

  const fetchEnv = useCallback(async () => {
    setEnvLoading(true);
    try {
      const res = await employeesApi.readFile(Number(id), 'home/env.ini');
      setEnvContent(res || '');
    } catch {
      setEnvContent('');
    }
    setEnvLoading(false);
  }, [id]);

  useEffect(() => {
    if (id) {
      addTab({ key: `/employees/${id}`, path: `/employees/${id}`, label: '员工详情', closable: true });
      fetchData(); fetchAllResources(); fetchEnv(); fetchUpdates();
    }
  }, [id, fetchData, fetchEnv, fetchUpdates]);

  useEffect(() => {
    if (employee?.install_status === 'installing') {
      pollInstallStatus();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee?.id, employee?.install_status]);

  const updateAssociation = async (field: string, ids: number[]) => {
    try {
      await employeesApi.update(Number(id), { [field]: ids });
      message.success('更新成功');
      fetchData();
    } catch (e: any) { message.error(e.response?.data?.detail || '更新失败'); }
  };

  const handleUpgradeResource = async (type: string, resourceId: number) => {
    try {
      await employeesApi.upgradeResource(Number(id), type, resourceId);
      message.success('已升级到最新版本');
      fetchData();
      fetchUpdates();
    } catch (e: any) { message.error(e.response?.data?.detail || '升级失败'); }
  };

  const makeAddHandler = (field: string, currentItems: any[]) => (newIds: number[]) => {
    updateAssociation(field, [...currentItems.map((x: any) => x.id), ...newIds]);
  };

  const makeRemoveHandler = (field: string, currentItems: any[]) => (removeId: number) => {
    updateAssociation(field, currentItems.filter((x: any) => x.id !== removeId).map((x: any) => x.id));
  };

  const handleUpdateDirectory = () => {
    setEmployee((prev: any) => ({ ...prev, install_status: 'installing' }));
    employeesApi.updateDirectory(Number(id))
      .catch((e: any) => {
        setEmployee((prev: any) => ({ ...prev, install_status: 'failed' }));
        message.error(e.response?.data?.detail || '安装失败');
      });
    message.info('安装已开始，请稍候...');
  };

  const clearInstallStatusTimer = () => {
    if (installStatusTimerRef.current) {
      clearInterval(installStatusTimerRef.current);
      installStatusTimerRef.current = null;
    }
  };

  const pollInstallStatus = () => {
    if (installStatusTimerRef.current) return;

    installStatusTimerRef.current = setInterval(async () => {
      try {
        const res: any = await employeesApi.get(Number(id));
        setEmployee(res);
        if (res.install_status === 'installed') {
          clearInstallStatusTimer();
          message.success('安装完成');
          setFileRefreshKey((k) => k + 1);
        } else if (res.install_status === 'failed') {
          clearInstallStatusTimer();
          message.error(res.install_error || '安装失败');
        }
      } catch {
        clearInstallStatusTimer();
      }
    }, 3000);
  };

  const handleDelete = async () => {
    try {
      await employeesApi.delete(Number(id));
      message.success('删除成功');
      window.dispatchEvent(new Event('employees-updated'));
      navigate('/employees');
    } catch (e: any) { message.error(e.response?.data?.detail || '删除失败'); }
  };

  const handleSaveSoul = async () => {
    try {
      await employeesApi.updateSoul(Number(id), editedSoul);
      message.success('SOUL.md 保存成功');
      setIsEditing(false);
      fetchData();
    } catch (e: any) { message.error(e.response?.data?.detail || '保存失败'); }
  };

  const handleGenerateSoul = () => {
    // 退出编辑模式
    setIsEditing(false);
    let collectedContent = '';
    
    streamGenerate({
      action: 'create_task',
      payload: { 
        key: 'generate_soul',
        id: Number(id),
        resourceName: `${employee?.name || '数字人'} - SOUL 生成`,
      },
      navigationUrl: `/employees/${id}`,
      onChunk: (chunk: string) => {
        collectedContent += chunk;
      },
      onDone: async () => {
        // 提取身份章节内容
        const identityMatch = collectedContent.match(/## 身份\s*\n([\s\S]*?)(?=\n## |$)/);
        const identityContent = identityMatch ? identityMatch[1].trim() : '';
        
        // 自动保存
        try {
          await employeesApi.updateSoul(Number(id), collectedContent);
          // 如果提取到身份内容，同时更新 description
          if (identityContent && employee) {
            await employeesApi.update(Number(id), { ...employee, description: identityContent });
          }
          fetchData();
        } catch (e: any) {
          message.error(e.response?.data?.detail || '保存失败');
        }
      }
    });
  };


  const openEditModal = () => {
    editForm.setFieldsValue({
      identifier: employee.identifier,
      name: employee.name,
      avatar: employee.avatar || '',
      employee_type: employee.employee_type,
      acp_backend: employee.acp_backend || 'hermes',
      description: employee.description,
      tags: employee.tags || [],
    });
    setEditModalVisible(true);
  };

  const handleEditSave = async (values: any) => {
    try {
      await employeesApi.update(Number(id), values);
      message.success('更新成功');
      setEditModalVisible(false);
      fetchData();
    } catch (e: any) { message.error(e.response?.data?.detail || '更新失败'); }
  };

  if (loading && !employee) {
    return (
      <div className="apple-page" style={{ textAlign: 'center', padding: '48px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="apple-page">
        <div className="apple-empty">
          <p className="apple-empty-title">未找到数字人</p>
          <Button type="primary" onClick={() => navigate('/employees')}>返回列表</Button>
        </div>
      </div>
    );
  }

  const skillIds: number[] = employee.skill_ids || [];
  const toolIds: number[] = employee.tool_ids || [];
  const mcpIds: number[] = employee.mcp_server_ids || [];
  const docIds: number[] = employee.document_ids || [];
  const expertIds: number[] = (employee.experts_info || []).map((e: any) => e.id);
  const teamMemberIds: number[] = employee.team_members || [];

  const skills = allSkills.filter((x: any) => skillIds.includes(x.id));
  const tools = allTools.filter((x: any) => toolIds.includes(x.id));
  const mcpServers = allMcp.filter((x: any) => mcpIds.includes(x.id));
  const documents = allDocs.filter((x: any) => docIds.includes(x.id));
  const experts = allExperts.filter((x: any) => expertIds.includes(x.id));
  const teamMembers = allEmployees.filter((x: any) => teamMemberIds.includes(x.id));
  const availableEmployees = allEmployees.filter((e: any) => e.id !== employee.id);

  const tabItems = [
    {
      key: 'intro',
      label: '介绍',
      children: (
        <>
          <Descriptions bordered column={1} size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="标识">{employee.identifier}</Descriptions.Item>
            <Descriptions.Item label="描述">{employee.description || '-'}</Descriptions.Item>
            <Descriptions.Item label="标签">
              {employee.tags?.map((t: string) => <Tag key={t} style={{ margin: '2px 4px 2px 0' }}>{t}</Tag>) || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={
              <Space size={4}>
                人工专家
                <Tooltip title="当数字人无法处理时，会挑选一个人工专家协助处理">
                  <InfoCircleOutlined style={{ color: 'var(--apple-text-secondary)', cursor: 'help' }} />
                </Tooltip>
              </Space>
            }>
              <Space size={4} wrap>
                {experts.map((e: any) => (
                  <Tooltip key={e.id} title={e.bio || '暂无描述'}>
                    <Tag
                      closable
                      closeIcon={
                        <span style={{ opacity: hoveredExpertId === e.id ? 1 : 0, transition: 'opacity 0.15s' }}>×</span>
                      }
                      onClose={(ev) => { ev.preventDefault(); makeRemoveHandler('expert_ids', experts)(e.id); }}
                      onMouseEnter={() => setHoveredExpertId(e.id)}
                      onMouseLeave={() => setHoveredExpertId(null)}
                      style={{ cursor: 'default' }}
                    >
                      {e.name}
                    </Tag>
                  </Tooltip>
                ))}
                <Tag
                  style={{ cursor: 'pointer', borderStyle: 'dashed' }}
                  icon={<PlusOutlined />}
                  onClick={() => {
                    const linkedIds = new Set(experts.map((x: any) => x.id));
                    const available = allExperts.filter((x: any) => !linkedIds.has(x.id));
                    setExpertPickerVisible(true);
                    setExpertPickerAvailable(available);
                  }}
                >
                  添加
                </Tag>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="模型配置">
              <Space size={4} style={{ cursor: 'pointer' }} onClick={() => setModelModalOpen(true)}>
                <span style={{ fontFamily: 'monospace', fontSize: 13 }}>{currentModelLabel || '-'}</span>
                <DownOutlined style={{ fontSize: 10, color: 'var(--apple-text-secondary)' }} />
              </Space>
            </Descriptions.Item>
          </Descriptions>

          {employee.soul_md ? (
            <Card
              title="SOUL.md"
              style={{ marginBottom: 16 }}
              styles={{ body: { padding: 0, overflow: 'hidden', borderRadius: '0 0 8px 8px' } }}
              extra={
                isEditing ? (
                  <Space>
                    <Button onClick={handleGenerateSoul}>AI 重新生成</Button>
                    <Button onClick={() => setIsEditing(false)}>取消</Button>
                    <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveSoul}>保存</Button>
                  </Space>
                ) : (
                  <Button type="text" icon={<EditOutlined />} onClick={() => { setEditedSoul(employee.soul_md || ''); setIsEditing(true); }} />
                )
              }
            >
              {isEditing ? (
                <MdEditor
                  value={editedSoul}
                  style={{ height: '500px' }}
                  renderHTML={(text) => mdParser.render(text)}
                  onChange={({ text }) => setEditedSoul(text)}
                  config={{
                    view: { menu: true, md: true, html: true },
                    canView: { menu: true, md: true, html: true, fullScreen: true, hideMenu: true },
                  }}
                />
              ) : (
                <div className="markdown-preview">
                  {employee.soul_md === '__generating__'
                    ? <div style={{ padding: 24, color: 'var(--apple-text-secondary)' }}><span>AI 生成中...</span></div>
                    : <ReactMarkdown remarkPlugins={[remarkGfm]}>{employee.soul_md}</ReactMarkdown>
                  }
                </div>
              )}
            </Card>
          ) : (
            <Card
              title="SOUL.md"
              style={{ marginBottom: 16 }}
              styles={{ body: { padding: 0, overflow: 'hidden', borderRadius: '0 0 8px 8px' } }}
              extra={
                isEditing ? (
                  <Space>
                    <Button onClick={handleGenerateSoul}>AI 重新生成</Button>
                    <Button onClick={() => setIsEditing(false)}>取消</Button>
                    <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveSoul}>保存</Button>
                  </Space>
                ) : (
                  <Button type="text" icon={<EditOutlined />} onClick={() => { setEditedSoul(''); setIsEditing(true); }} />
                )
              }
            >
              {isEditing ? (
                <MdEditor
                  value={editedSoul}
                  style={{ height: '500px' }}
                  renderHTML={(text) => mdParser.render(text)}
                  onChange={({ text }) => setEditedSoul(text)}
                  config={{
                    view: { menu: true, md: true, html: true },
                    canView: { menu: true, md: true, html: true, fullScreen: true, hideMenu: true },
                  }}
                />
              ) : (
                <div className="markdown-preview" style={{ padding: 24, color: 'var(--apple-text-secondary)' }}>
                  {employee.soul_md === '__generating__' ? 'AI 生成中...' : '暂无内容，点击编辑添加'}
                </div>
              )}
            </Card>
          )}
          <ModelConfigModal
            open={modelModalOpen}
            employeeId={Number(id)}
            allowedProviders={employee?.allowed_providers}
            onClose={() => setModelModalOpen(false)}
            onApplied={(profile, model) => setCurrentModelLabel(model)}
          />
        </>
      ),
    },
    {
      key: 'resources',
      label: '资源',
      children: (
        <Tabs
          tabPosition="left"
          items={[
            ...(employee.employee_type === 'manager' ? [{
              key: 'team',
              label: '团队成员',
              children: (
                <ResourceSection
                  title="团队成员"
                  linked={teamMembers}
                  all={availableEmployees}
                  onAdd={makeAddHandler('team_members', teamMembers)}
                  onRemove={makeRemoveHandler('team_members', teamMembers)}
                />
              ),
            }] : []),
            {
              key: 'skills',
              label: 'Skill 技能',
              children: (
                <ResourceSection
                  title="Skill 技能"
                  linked={skills}
                  all={allSkills}
                  nameKey="title"
                  onAdd={makeAddHandler('skill_ids', skills)}
                  onRemove={makeRemoveHandler('skill_ids', skills)}
                  onView={async (record) => {
                    const installed = (employee.installed_skills || []).find((s: any) => s.name === record.title);
                    if (installed?.path) {
                      try {
                        const content = await employeesApi.readFile(Number(id), installed.path);
                        setPreviewSkill({ ...installed, content });
                      } catch {
                        setPreviewSkill(installed);
                      }
                    } else {
                      setPreviewSkill({ name: record.title, content: '该技能尚未安装，请先点击「更新」' });
                    }
                  }}
                  onUpgrade={(resId) => handleUpgradeResource('skills', resId)}
                  resourceType="skills"
                  resourceVersions={employee.resource_versions?.skills}
                  updates={resourceUpdates.filter((u: any) => u.type === 'skills')}
                />
              ),
            },
            {
              key: 'tools',
              label: 'Tool 工具',
              children: (
                <ResourceSection
                  title="Tool 工具"
                  linked={tools}
                  all={allTools}
                  nameKey="title"
                  onAdd={makeAddHandler('tool_ids', tools)}
                  onRemove={makeRemoveHandler('tool_ids', tools)}
                  onView={async (record) => {
                    const installed = (employee.installed_skills || []).find((s: any) => s.name === record.title);
                    if (installed?.path) {
                      try {
                        const content = await employeesApi.readFile(Number(id), installed.path);
                        setPreviewSkill({ ...installed, content });
                      } catch { setPreviewSkill(installed); }
                    } else {
                      setPreviewSkill({ name: record.title, content: '该工具尚未安装，请先点击「安装」' });
                    }
                  }}
                  onUpgrade={(resId) => handleUpgradeResource('tools', resId)}
                  resourceType="tools"
                  resourceVersions={employee.resource_versions?.tools}
                  updates={resourceUpdates.filter((u: any) => u.type === 'tools')}
                />
              ),
            },
            {
              key: 'mcp',
              label: 'MCP Server',
              children: (
                <ResourceSection
                  title="MCP Server"
                  linked={mcpServers}
                  all={allMcp}
                  nameKey="title"
                  onAdd={makeAddHandler('mcp_server_ids', mcpServers)}
                  onRemove={makeRemoveHandler('mcp_server_ids', mcpServers)}
                  extraActions={
                    <Button size="small" icon={<EyeOutlined />} onClick={async () => {
                      try {
                        const raw: string = await employeesApi.readFile(Number(id), 'config.yaml');
                        // Extract mcp_servers block: the key line + all following indented lines
                        const lines = raw.split('\n');
                        const start = lines.findIndex(l => /^mcp_servers\s*:/.test(l));
                        let content: string;
                        if (start === -1) {
                          content = '# config.yaml 中暂无 mcp_servers 配置';
                        } else {
                          const block = [lines[start]];
                          for (let i = start + 1; i < lines.length; i++) {
                            if (lines[i] === '' || /^\s/.test(lines[i])) { block.push(lines[i]); } else { break; }
                          }
                          content = block.join('\n').trimEnd();
                        }
                        setPreviewSkill({ name: 'config.yaml - mcp_servers', content: '```yaml\n' + content + '\n```' });
                      } catch {
                        setPreviewSkill({ name: 'config.yaml - mcp_servers', content: '读取 config.yaml 失败' });
                      }
                    }}>预览</Button>
                  }
                  onUpgrade={(resId) => handleUpgradeResource('mcps', resId)}
                  resourceType="mcps"
                  resourceVersions={employee.resource_versions?.mcps}
                  updates={resourceUpdates.filter((u: any) => u.type === 'mcps')}
                />
              ),
            },
            {
              key: 'documents',
              label: '文档管理',
              children: (
                <ResourceSection
                  title="文档管理"
                  linked={documents}
                  all={allDocs}
                  nameKey="title"
                  onAdd={makeAddHandler('document_ids', documents)}
                  onRemove={makeRemoveHandler('document_ids', documents)}
                  onView={async (record) => {
                    const name = record.title || record.name;
                    const safeName = name.replace(/[\\/:*?"<>|\s]+/g, '_').replace(/^_+|_+$/g, '') || 'unnamed';
                    const installed = (employee.installed_skills || []).find(
                      (s: any) => s.name === name || s.name === safeName
                    );
                    if (installed?.path) {
                      try {
                        const content = await employeesApi.readFile(Number(id), installed.path);
                        setPreviewSkill({ ...installed, content });
                      } catch { setPreviewSkill(installed); }
                    } else {
                      setPreviewSkill({ name, content: '该文档尚未安装，请先点击「安装」' });
                    }
                  }}
                  onUpgrade={(resId) => handleUpgradeResource('documents', resId)}
                  resourceType="documents"
                  resourceVersions={employee.resource_versions?.documents}
                  updates={resourceUpdates.filter((u: any) => u.type === 'documents')}
                />
              ),
            },
          ]}
        />
      ),
    },
    {
      key: 'env',
      label: '环境变量',
      children: (
        <Card
          title={
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              env.ini
              <Tooltip title="在 SKILL 中引入方式为：$HOME/env.ini" overlayStyle={{ maxWidth: 340 }}>
                <InfoCircleOutlined style={{ color: '#999', fontSize: 13, cursor: 'pointer' }} />
              </Tooltip>
            </span>
          }
          styles={{ body: { padding: 0, overflow: 'hidden', borderRadius: '0 0 8px 8px' } }}
          extra={
            <Space>
              <Button icon={<ImportOutlined />} onClick={async () => {
                try { const res: any = await featureConfigsApi.list(); setFeatureConfigs(res); } catch {}
                setImportModalVisible(true);
              }}>导入配置</Button>
              <Button type="primary" icon={<SaveOutlined />} onClick={async () => {
                const newContent = iniFormRef.current?.getContent();
                if (newContent != null) {
                  try {
                    await employeesApi.writeFile(Number(id), 'home/env.ini', newContent);
                    setEnvContent(newContent);
                    message.success('环境变量保存成功');
                  } catch { message.error('保存失败'); }
                }
              }}>保存</Button>
            </Space>
          }
        >
          {envLoading ? <Spin style={{ padding: 24, display: 'block' }} /> : (
            <IniFormView ref={iniFormRef} content={envContent} onViewDoc={(sectionName) => {
              const fc = featureConfigs.find((f: any) => f.config_ini?.includes(`[${sectionName}]`));
              if (fc?.config_doc) {
                setPreviewSkill({ name: `${sectionName} - 配置说明`, content: fc.config_doc });
              } else {
                featureConfigsApi.list().then((res: any) => {
                  setFeatureConfigs(res);
                  const found = res.find((f: any) => f.config_ini?.includes(`[${sectionName}]`));
                  if (found?.config_doc) {
                    setPreviewSkill({ name: `${sectionName} - 配置说明`, content: found.config_doc });
                  } else {
                    message.info('该功能暂无配置说明');
                  }
                }).catch(() => message.info('该功能暂无配置说明'));
              }
            }} />
          )}
        </Card>
      ),
    },
    {
      key: 'files',
      label: '文件',
      children: (
        <Card>
          <AppFileManager
            key={fileRefreshKey}
            appId={Number(id)}
            fileApi={employeesApi}
            defaultExpandAll={false}
            initialFile={employee?.file_visible_names ? 'SOUL.md' : undefined}
            allowedNames={employee?.file_visible_names || undefined}
          />
        </Card>
      ),
    },
    {
      key: 'command_allowlist',
      label: '命令权限',
      children: (
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          {permissionRequests.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--apple-text-secondary)' }}>
              <div style={{ fontSize: 16, marginBottom: 8 }}>暂无权限请求记录</div>
              <div style={{ fontSize: 13 }}>当数字人调用需要授权的工具时，会自动创建权限请求</div>
            </div>
          ) : (
            <>
              {/* 待审批 */}
              {permissionRequests.filter((r: any) => r.status === 'pending').length > 0 && (
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--apple-text-secondary)', marginBottom: 8 }}>
                    待审批请求 <Tag color="orange">{permissionRequests.filter((r: any) => r.status === 'pending').length}</Tag>
                  </div>
                  <Space direction="vertical" style={{ width: '100%' }} size={8}>
                    {permissionRequests.filter((r: any) => r.status === 'pending').map((row: any) => (
                      <Card key={row.id} size="small" style={{ borderRadius: 10, borderColor: '#fa8c16' }} bodyStyle={{ padding: '14px 16px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '64px 1fr', rowGap: 6, columnGap: 8, fontSize: 13 }}>
                          <span style={{ color: 'var(--apple-text-secondary)', fontSize: 12, alignSelf: 'center' }}>工具</span>
                          <span><Tag style={{ margin: 0 }}>{row.tool_name}</Tag></span>
                          <span style={{ color: 'var(--apple-text-secondary)', fontSize: 12, paddingTop: 2 }}>描述</span>
                          <span><ExpandableText text={row.title} /></span>
                          <span style={{ color: 'var(--apple-text-secondary)', fontSize: 12, alignSelf: 'center' }}>时间</span>
                          <span style={{ color: 'var(--apple-text-secondary)' }}>{new Date(row.created_at).toLocaleString()}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--apple-border)' }}>
                          <Button size="small" type="primary" onClick={async () => {
                            try {
                              const res: any = await employeesApi.approvePermissionRequest(Number(id), row.id);
                              setCommandAllowlist(res.command_allowlist || []);
                              setPermissionRequests(prev => prev.map((r: any) => r.id === row.id ? { ...r, status: 'approved' } : r));
                              message.success(`已允许 ${row.tool_name}`);
                            } catch { message.error('操作失败'); }
                          }}>允许</Button>
                          <Button size="small" danger onClick={async () => {
                            try {
                              await employeesApi.denyPermissionRequest(Number(id), row.id);
                              setPermissionRequests(prev => prev.map((r: any) => r.id === row.id ? { ...r, status: 'denied' } : r));
                              message.success('已拒绝');
                            } catch { message.error('操作失败'); }
                          }}>拒绝</Button>
                        </div>
                      </Card>
                    ))}
                  </Space>
                </div>
              )}

              {/* 已允许 */}
              {permissionRequests.filter((r: any) => ['approved', 'allowed'].includes(r.status)).length > 0 && (
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--apple-text-secondary)', marginBottom: 8 }}>
                    权限申请 <Tag color="green">{permissionRequests.filter((r: any) => ['approved', 'allowed'].includes(r.status)).length}</Tag>
                  </div>
                  <Space direction="vertical" style={{ width: '100%' }} size={8}>
                    {permissionRequests.filter((r: any) => ['approved', 'allowed'].includes(r.status)).map((row: any) => (
                      <Card key={row.id} size="small" style={{ borderRadius: 10, borderColor: '#52c41a' }} bodyStyle={{ padding: '14px 16px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '64px 1fr', rowGap: 6, columnGap: 8, fontSize: 13 }}>
                          <span style={{ color: 'var(--apple-text-secondary)', fontSize: 12, alignSelf: 'center' }}>工具</span>
                          <span><Tag color="green" style={{ margin: 0 }}>{row.tool_name}</Tag></span>
                          <span style={{ color: 'var(--apple-text-secondary)', fontSize: 12, paddingTop: 2 }}>描述</span>
                          <span><ExpandableText text={row.title} /></span>
                          <span style={{ color: 'var(--apple-text-secondary)', fontSize: 12, alignSelf: 'center' }}>状态</span>
                          <span><Tag color="green" style={{ margin: 0 }}>{row.status === 'approved' ? '手动批准' : '自动允许'}</Tag></span>
                          <span style={{ color: 'var(--apple-text-secondary)', fontSize: 12, alignSelf: 'center' }}>批准时间</span>
                          <span style={{ color: 'var(--apple-text-secondary)' }}>{new Date(row.created_at).toLocaleString()}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--apple-border)' }}>
                          <Button size="small" danger onClick={async () => {
                            try {
                              const res: any = await employeesApi.revokePermissionRequest(Number(id), row.id);
                              setCommandAllowlist(res.command_allowlist || []);
                              setPermissionRequests(prev => prev.map((r: any) => r.id === row.id ? { ...r, status: 'revoked' } : r));
                              message.success(`已撤销 ${row.tool_name} 权限`);
                            } catch { message.error('操作失败'); }
                          }}>撤销</Button>
                        </div>
                      </Card>
                    ))}
                  </Space>
                </div>
              )}

              {/* 历史记录 */}
              {permissionRequests.filter((r: any) => ['denied', 'revoked'].includes(r.status)).length > 0 && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--apple-text-secondary)' }}>历史记录</span>
                    <Button size="small" danger onClick={async () => {
                      const historyIds = permissionRequests.filter((r: any) => ['denied', 'revoked'].includes(r.status)).map((r: any) => r.id);
                      try {
                        await employeesApi.deletePermissionRequests(Number(id), historyIds);
                        setPermissionRequests(prev => prev.filter((r: any) => !historyIds.includes(r.id)));
                        message.success('已清理历史记录');
                      } catch { message.error('清理失败'); }
                    }}>清理历史</Button>
                  </div>
                  <Space direction="vertical" style={{ width: '100%' }} size={8}>
                    {permissionRequests.filter((r: any) => ['denied', 'revoked'].includes(r.status)).map((row: any) => (
                      <Card key={row.id} size="small" style={{ borderRadius: 10, borderColor: '#d9d9d9', opacity: 0.65 }} bodyStyle={{ padding: '14px 16px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '64px 1fr', rowGap: 6, columnGap: 8, fontSize: 13 }}>
                          <span style={{ color: 'var(--apple-text-secondary)', fontSize: 12, alignSelf: 'center' }}>工具</span>
                          <span><Tag style={{ margin: 0 }}>{row.tool_name}</Tag></span>
                          <span style={{ color: 'var(--apple-text-secondary)', fontSize: 12, paddingTop: 2 }}>描述</span>
                          <span><ExpandableText text={row.title} /></span>
                          <span style={{ color: 'var(--apple-text-secondary)', fontSize: 12, alignSelf: 'center' }}>状态</span>
                          <span><Tag color={row.status === 'denied' ? 'red' : 'default'} style={{ margin: 0 }}>{row.status === 'denied' ? '已拒绝' : '已撤销'}</Tag></span>
                          <span style={{ color: 'var(--apple-text-secondary)', fontSize: 12, alignSelf: 'center' }}>时间</span>
                          <span style={{ color: 'var(--apple-text-secondary)' }}>{new Date(row.created_at).toLocaleString()}</span>
                        </div>
                      </Card>
                    ))}
                  </Space>
                </div>
              )}
            </>
          )}
        </Space>
      ),
    },
    {
      key: 'gateway',
      label: '网关',
      children: <GatewayTab employeeId={employee.id} />,
    },
  ];

  if (!employee) return <Spin />;

  return (
    <div className="apple-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Space align="center">
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/employees')} />
          <h1 className="apple-page-title" style={{ marginBottom: 0 }}>{employee.name}</h1>
        </Space>
        <Space>
          <Button icon={<EditOutlined />} onClick={openEditModal}>编辑</Button>
          <Badge dot={employee.needs_reinstall && employee.install_status !== 'installing'} offset={[-4, 4]}>
            <Button
              icon={<CloudDownloadOutlined />}
              onClick={handleUpdateDirectory}
              loading={employee.install_status === 'installing'}
              type={employee.needs_reinstall ? 'primary' : 'default'}
            >
              {employee.install_status === 'installing' ? '安装中' : '安装'}
            </Button>
          </Badge>
          <Popconfirm
            title="确认删除"
            description="确定要删除此数字人吗？将删除其 Profile 目录和数据库记录。"
            onConfirm={handleDelete}
            okText="确定"
            cancelText="取消"
          >
            <Button danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      </div>

      <Tabs items={tabItems} />

      {/* Expert Picker Modal */}
      <Modal
        title="添加人工专家"
        open={expertPickerVisible}
        onCancel={() => { setExpertPickerVisible(false); setExpertPickerSelected([]); }}
        onOk={() => {
          if (expertPickerSelected.length > 0) makeAddHandler('expert_ids', experts)(expertPickerSelected);
          setExpertPickerVisible(false);
          setExpertPickerSelected([]);
        }}
        okText="添加"
        destroyOnHidden
      >
        {expertPickerAvailable.length === 0 ? (
          <p style={{ color: 'var(--apple-text-secondary)' }}>没有可添加的项目</p>
        ) : (
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {expertPickerAvailable.map((item: any) => (
              <div key={item.id} style={{ padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                <Checkbox
                  checked={expertPickerSelected.includes(item.id)}
                  onChange={(e) => setExpertPickerSelected(e.target.checked
                    ? [...expertPickerSelected, item.id]
                    : expertPickerSelected.filter((id) => id !== item.id))}
                >
                  <span>{item.name}</span>
                  {item.bio && <div style={{ fontSize: 12, color: 'var(--apple-text-secondary)', marginTop: 2 }}>{item.bio}</div>}
                </Checkbox>
              </div>
            ))}
          </div>
        )}
      </Modal>
      {/* Edit Modal */}
      <Modal
        className="apple-modal"
        title="编辑数字人"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={() => editForm.submit()}
        destroyOnHidden
      >
        <Form className="apple-form" form={editForm} layout="vertical" onFinish={handleEditSave}>
          <Form.Item name="avatar" label="头像">
            <ImagePicker shape="circle" />
          </Form.Item>
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="数字人名称" />
          </Form.Item>
          <Form.Item name="employee_type" label="类型" rules={[{ required: true }]}>
            <Select options={employeeTypeOptions} />
          </Form.Item>
          <Form.Item name="acp_backend" label="ACP 后端">
            <Select options={acpBackendOptions} />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <TextArea rows={3} placeholder="描述数字人的职责和能力" />
          </Form.Item>
          <Form.Item name="tags" label="分类标签">
            <Select mode="multiple" placeholder="选择或输入分类标签" options={employeeCategoryOptions} allowClear />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        open={!!previewSkill}
        title={previewSkill?.name}
        onCancel={() => setPreviewSkill(null)}
        footer={null}
        width="90vw"
        style={{ top: 20 }}
        styles={{ body: { height: '80vh', overflow: 'auto' } }}
      >
        {previewSkill && (
          <div className="markdown-preview">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{(previewSkill.content || '暂无内容').replace(/^---\n([\s\S]*?)---\n*/, (_: string, fm: string) => {
              const name = fm.match(/name:\s*(.*)/)?.[1] || '';
              const desc = fm.match(/description:\s*(.*)/)?.[1] || '';
              return `# ${name}\n${desc ? desc + '\n\n' : '\n'}---\n\n`;
            })}</ReactMarkdown>
          </div>
        )}
      </Modal>
      {/* Import Feature Config Modal */}
      <Modal
        title="导入功能配置"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={<Button type="link" size="small" onClick={() => { setImportModalVisible(false); navigate('/feature-config'); }}>＋ 去新建配置</Button>}
        destroyOnHidden
      >
        {featureConfigs.length === 0 ? (
          <p style={{ color: 'var(--apple-text-secondary)' }}>暂无功能配置</p>
        ) : (
          <Table
            size="small"
            dataSource={featureConfigs}
            rowKey="id"
            pagination={false}
            columns={[
              { title: '功能名称', dataIndex: 'name' },
              { title: '版本', dataIndex: 'version', width: 60 },
              { title: '操作', width: 80, render: (_: any, r: any) => (
                <Button type="link" size="small" onClick={async () => {
                  const append = r.config_ini || '';
                  const current = iniFormRef.current?.getContent() || envContent;
                  // 按section名去重：提取已有section名和待导入section名
                  const existingSections = new Set((current.match(/^\[(.+)\]$/gm) || []).map((s: string) => s.slice(1, -1)));
                  const filtered = append.split(/(?=^\[)/m).filter((block: string) => {
                    const m = block.match(/^\[(.+)\]/);
                    return !m || !existingSections.has(m[1]);
                  }).join('');
                  if (!filtered.trim()) { message.info('所有配置项已存在，无需重复导入'); return; }
                  const newContent = current ? current.trimEnd() + '\n\n' + filtered.trim() : filtered.trim();
                  try {
                    await employeesApi.writeFile(Number(id), 'home/env.ini', newContent);
                    setEnvContent(newContent);
                    message.success(`已导入「${r.name}」配置`);
                    setImportModalVisible(false);
                  } catch { message.error('导入失败'); }
                }}>导入</Button>
              )},
            ]}
          />
        )}
      </Modal>
    </div>
  );
};

export default EmployeeDetail;
